import React, { forwardRef } from "react";
import CloseIcon from "@mui/icons-material/Close";

/**
 * The frame every admin modal shares.
 *
 * The pages open these inside an MUI <Modal>, which centres them and passes a
 * ref to its child - hence forwardRef. The old modals each re-declared a
 * `w-2/5` rounded-2xl panel with a bare <CloseSharp> icon floated into the
 * corner (not focusable, no label, drifting position under RTL). This gives
 * them one titled header, a real close <button>, a scroll-capped body, and a
 * width that actually works on a phone.
 *
 * Props:
 *   title    - heading text
 *   subtitle - optional line under the title
 *   close    - handler for the close button and backdrop parity
 *   width    - Tailwind max-width class (default max-w-[52rem])
 *   footer   - optional pinned footer node (e.g. the submit button row)
 */
const ModalShell = forwardRef(
    (
        {
            title,
            subtitle,
            close,
            width = "max-w-[52rem]",
            footer,
            children,
        },
        ref
    ) => {
        return (
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                className={`relative m-lg flex max-h-[88vh] w-full flex-col rounded-panel bg-surface text-start shadow-headerCard focus:outline-none ${width}`}
            >
                <div className="flex items-start justify-between gap-md border-b border-rule-subtle p-xl">
                    <div className="min-w-0">
                        <h2 className="text-md font-semibold text-ink">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="mt-2xs text-sm text-ink-subtle">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        aria-label="بستن"
                        className="flex h-[3.2rem] w-[3.2rem] shrink-0 items-center justify-center rounded-soft text-ink-subtle transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    >
                        <CloseIcon sx={{ fontSize: "2rem" }} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-xl">
                    {children}
                </div>

                {footer && (
                    <div className="border-t border-rule-subtle p-xl">
                        {footer}
                    </div>
                )}
            </div>
        );
    }
);

export default ModalShell;
